import type { JobsDetailData, JobsListParams, JobsReqParams } from '@contracts'
import { ColDef } from 'ag-grid-community'
import StatusCell from 'components/table/StatusCell'
import TitleWithSubtitleCell from 'components/table/TitleWithSubtitleCell'
import { FilterLoadData } from 'context/appContext/appContext'

import {
  FilterComponentData,
  InputFilterComponentData,
  SelectFilterComponentData,
  IdValuePair,
} from 'reducers/GenericFiltersReducer/GenericFiltersReducer.d'
import { getFormattedDate, getType, getTypeKey, transformTypesToTaskType } from 'utils'
import { unknown } from 'utils/constants'
import type { ClientAndBrandName, singleClientV5 } from 'utils/utils.d'

export type JobWithClientAndBrand = JobsDetailData & ClientAndBrandName

export function getJobFieldKey(arg: keyof JobWithClientAndBrand) {
  return getTypeKey<JobWithClientAndBrand>(arg)
}

export const columnStructure: ColDef<JobWithClientAndBrand>[] = [
  {
    headerName: 'Product',
    field: getJobFieldKey('reference_name'),
    sortable: true,
    flex: 1,
    resizable: true,
    cellRenderer: ({ data }: { data: JobWithClientAndBrand }) => (
      <TitleWithSubtitleCell title={data?.reference_name || unknown} subTitle={data?.group_id || unknown} />
    ),
  },
  { headerName: 'Product ID', field: getJobFieldKey('product_id'), sortable: true, flex: 1, resizable: true },
  {
    headerName: 'Project',
    field: getJobFieldKey('project_name'),
    sortable: true,
    flex: 1,
    resizable: true,
    cellRenderer: ({ data }: { data: JobWithClientAndBrand }) => (
      <TitleWithSubtitleCell
        title={data?.project_name || unknown}
        subTitle={`${data.client_name} | ${data.brand_name}`}
      />
    ),
  },
  {
    headerName: 'Retailer',
    field: getJobFieldKey('retailer_name'),
    colId: 'retailer_id',
    sortable: true,
    flex: 1,
    resizable: true,
  },
  {
    headerName: 'Task Type',
    field: getJobFieldKey('type'),
    sortable: true,
    flex: 1,
    resizable: true,
    valueFormatter: ({ value }) => transformTypesToTaskType(value),
  },
  {
    headerName: 'Created',
    field: getJobFieldKey('created_at'),
    sortable: true,
    flex: 1,
    resizable: true,
    valueFormatter: date => getFormattedDate(date.value),
  },
  {
    field: getJobFieldKey('status'),
    sortable: true,
    flex: 1,
    resizable: true,
    cellRenderer: ({ value }: { value: JobWithClientAndBrand['status'] }) => <StatusCell status={value} />,
  },
]

/**
 * Filters
 */

export const defaultSelectFilterFields: Pick<SelectFilterComponentData, 'type' | 'options' | 'activeFilters'> = {
  type: 'select',
  options: [],
  activeFilters: [],
}

export const defaultInputFilterFields: Pick<InputFilterComponentData, 'type' | 'activeFilters' | 'isMultiSelect'> = {
  type: 'text',
  activeFilters: [],
  isMultiSelect: true,
}

export const defaultClientFilter: SelectFilterComponentData = {
  ...defaultSelectFilterFields,
  id: 'clients',
  labelText: 'Client',
  isMultiSelect: true,
  options: [{ id: 'all', value: 'All' }],
  defaultValue: { id: 'all', value: 'All' },
}

export const defaultBrandFilter: SelectFilterComponentData = {
  ...defaultSelectFilterFields,
  id: 'brands',
  labelText: 'Brand',
  isMultiSelect: true,
  options: [{ id: 'all', value: 'All' }],
  defaultValue: { id: 'all', value: 'All' },
}

export const defaultRegionsFilter: SelectFilterComponentData = {
  ...defaultSelectFilterFields,
  id: 'region_ids',
  labelText: 'Regions',
  isMultiSelect: true,
  options: [{ id: 'all', value: 'All' }],
  defaultValue: { id: 'all', value: 'All' },
}

export const defaultProjectNameFilter: InputFilterComponentData = {
  ...defaultInputFilterFields,
  id: getType<keyof JobsListParams>('projects'),
  labelText: 'Project',
  placeholder: 'i.e. Spring Fresh',
}

export const defaultRetailerFilter: SelectFilterComponentData = {
  ...defaultSelectFilterFields,
  id: 'retailers',
  labelText: 'Retailer',
  isMultiSelect: true,
  options: [{ id: 'all', value: 'All' }],
  defaultValue: { id: 'all', value: 'All' },
}

export const defaultGroupIdFilter: InputFilterComponentData = {
  ...defaultInputFilterFields,
  id: 'group_ids',
  labelText: 'Group ID',
  placeholder: 'i.e. 1000000',
}

export const defaultReferenceNameFilter: InputFilterComponentData = {
  ...defaultInputFilterFields,
  id: 'reference_names',
  labelText: 'Product',
  placeholder: 'i.e. Nagellack Gel',
}

export const defaultProductIdFilter: InputFilterComponentData = {
  ...defaultInputFilterFields,
  id: 'product_ids',
  labelText: 'Product ID',
  placeholder: 'i.e. 978123456789',
}

export const defaultTaskTypeFilter: SelectFilterComponentData = {
  ...defaultSelectFilterFields,
  id: 'types',
  labelText: 'Task Type',
  isMultiSelect: true,
  options: [
    { id: 'all', value: 'All' },
    { id: 'standard_content', value: transformTypesToTaskType('standard_content') },
    { id: 'retailer_adaption', value: transformTypesToTaskType('retailer_adaption') },
    { id: 'sku_adaption', value: transformTypesToTaskType('sku_adaption') },
  ],
  defaultValue: { id: 'all', value: 'All' },
}

export const defaultStatusFilter: SelectFilterComponentData = {
  ...defaultSelectFilterFields,
  id: 'statuses',
  labelText: 'Status',
  isMultiSelect: true,
  options: [
    { id: 'all', value: 'All' },
    { id: 'blocked', value: 'Queued' },
    { id: 'executing', value: 'Executing' },
    { id: 'processing', value: 'Processing' },
    { id: 'ready_for_review', value: 'Pending approval' },
    { id: 'completed', value: 'Completed' },
    { id: 'failed', value: 'Failed' },
  ],
  defaultValue: { id: 'all', value: 'All' },
}

export const tableFilters: FilterComponentData[] = [
  defaultClientFilter,
  defaultBrandFilter,
  defaultRetailerFilter,
  defaultTaskTypeFilter,
  defaultStatusFilter,
  defaultProjectNameFilter,
  defaultGroupIdFilter,
  defaultReferenceNameFilter,
  defaultProductIdFilter,
]

/**
 * Extract the desired field from an object array, returning an IdValuePair array with the id persisting from the original object
 *
 * @param data - The array of objects to extract the desired field values from
 * @param fieldKey - A key of the object type used in the data paraeter, this is used to extract the desired field value
 * @returns An IdValuePair array with the id persisting from the original object and the value extracted from the desired field
 * @template T - An array of objects to extract the field from (must contain an id field).
 */
export const extractFieldFromData = <T extends { id?: string; _id?: string }>(
  data: T[],
  fieldKey: keyof T,
): IdValuePair[] => {
  return data.map(item => ({
    id: item.id || item._id,
    value: item[fieldKey],
  })) as IdValuePair[]
}

export const getPreservedFilterState = (
  currentFilterSelection: FilterLoadData[],
  filterEntity: keyof JobsReqParams,
) => {
  return currentFilterSelection?.find(({ filterId }) => filterId === filterEntity)
}

/**
 * returns a FilterLoadData object to be used in the client filter
 *
 * @param clients - The full list of clients
 * @param activeClientIds - The selected clientIds
 * @returns a FilterLoadData object array, used to pre-select thee clients when coming to the page
 */
export const getClientObjects = (matchedClients: singleClientV5[]): FilterLoadData | null => {
  return matchedClients.length > 0
    ? {
        filterId: 'clients',
        activeFilters: extractFieldFromData(matchedClients, 'client_name'),
      }
    : null // if no clients are selected, return empty array
}

/**
 * Extract the parameters required to construct the filter select elements, when dealing with dynamic data (options loaded from an API etc.)
 *
 * @param optionLabelKey - A key of the object type used in the apiObjectArray paraeter, this is used as the label for the select options
 * @param apiObjectArray - The array of objects to construct the select options from
 * @param preservedFilterSelection - The preserved filter selection pertaining to the filter, stored in context
 * @returns The options extracted from the API data, and any active filters to be applied on load, from the preserved filter selection
 * @example
 * ```typescript
 * Example usage of the function
 * const clients: singleClientV5[] = [{ id: '1', client_name: 'loreal', ... }, { id: '2', client_name: 'colgate', ... }];
 * const preservedClientFilters: FilterLoadData = { filterId: 'clients', activeFilters: [{ id: '1', value: 'loreal' }] };
 * const result = getFilterElementInitilisationObject<singleClientV5>(
 *    'client_name',
 *    clients,
 *    preservedClientFilters,
 * )
 * result;
 * // { currentFilterComponent: { options: [{ id: '1', value: 'loreal' }, { id: '2', value: 'colgate' }], activeFilters: [{ id: 1, value: 'loreal' }] }
 * ```
 * @template T - An array of objects to extract the filter options from (must contain an id field).
 */
export function getFilterElementInitFields<T extends { id?: string | undefined }>(
  optionLabelKey: keyof T,
  apiObjectArray: T[],
  preservedFilterSelection: FilterLoadData | null,
) {
  const selectElementOptions = extractFieldFromData(apiObjectArray, optionLabelKey)

  // Construct a partial of the filter state to initialise the filter component with
  const initialisedFilterStatePartial: Pick<SelectFilterComponentData, 'options' | 'activeFilters'> = {
    options: selectElementOptions,
    activeFilters: preservedFilterSelection ? (preservedFilterSelection.activeFilters as IdValuePair[]) : [],
  }
  return {
    currentFilterComponent: initialisedFilterStatePartial,
  }
}

export const brandFilterReducerEmptyDataMessage =
  'There are currently no tasks available to view for the current selection of brands'
export const tableFilterReducerEmptyDataMessage =
  'There are currently no tasks available to view for the current filter selection'
export const projectSummaryFilterReducerEmptyDataMessage =
  'There are currently no tasks available to view for the current filter selection'
export const regionSettingsEmptyDataMessage = 'There are currently no regions to view'
export const retailerSettingsEmptyDataMessage = 'There are currently no retailers to view'
export const standardContentSettingsEmptyDataMessage = 'There are currently no standard content entities to view'
